// Derived from https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role

import { KEY_CODES } from '../util';

class Tabs {
  constructor() {
    this.tabs = document.querySelectorAll('[role=tab]');
    this.tabList = document.querySelector('[role="tablist"]');
    this.tabMap = {};
    this.tabFocus = 0;

    // Create an object where the where the keys are the hash
    // and the values are the corresponding HTML elements
    // i.e. { #minors: button#tab-minors.tabs__btn.js--tabs__btn }
    Array.from(this.tabs).forEach(tab => {
      const hash = this.getTabHashFromId(tab.id);
      this.tabMap[hash] = tab;
    });

    this.bindEvents();
  }

  // Example: 'tab-foo' -> '#foo'
  getTabHashFromId(id) {
    return `#${id.replace('tab-', '')}`;
  }

  bindEvents() {
    window.addEventListener('load', () => this.handleHash(), false);
    window.addEventListener('hashchange', () => this.handleHash(), false);
    Array.from(this.tabs).forEach(tab => tab.addEventListener('click', e => this.changeTabs(e)));
    this.tabList.addEventListener('keydown', e => this.handleKeydown(e));
  }

  // Programmatically clicks a tab depending on the location hash
  handleHash() {
    const { hash } = window.location;

    if (hash) {
      setTimeout(() => {
        const tabEl = this.tabMap[hash];
        if (tabEl) {
          tabEl.click();
        }
      }, 0);
    }
  }

  // Change the active tab and tabpanel
  changeTabs(e) {
    const target = e.target;
    const tablist = target.closest('[role="tablist"]');
    const tablistContainer = tablist.parentNode;

    // Remove all current selected tabs
    Array.from(tablist.querySelectorAll('[aria-selected="true"]')).forEach(t =>
      t.setAttribute('aria-selected', false),
    );

    // Set this tab as selected
    target.setAttribute('aria-selected', true);

    // Hide all tab panels
    Array.from(tablistContainer.querySelectorAll('[role="tabpanel"]')).forEach(p =>
      p.setAttribute('hidden', true),
    );

    // Show the selected panel
    tablistContainer
      .querySelector(`#${target.getAttribute('aria-controls')}`)
      .removeAttribute('hidden');

    // Change URL based on selected tab
    if (window.history && history.pushState) {
      const hash = this.getTabHashFromId(target.id);
      history.replaceState({}, '', hash);
    }
  }

  // Enable arrow navigation between tabs in the tab list
  handleKeydown(e) {
    // If the right arrow or left arrow is pressed, change focus accordingly
    switch (e.key) {
      case KEY_CODES.ARROW_RIGHT:
      case KEY_CODES.ARROW_RIGHT_IE11:
        // Make the current tab element untabbable
        this.tabs[this.tabFocus].setAttribute('tabindex', -1);
        // Move focus to the right
        this.tabFocus++;
        // If we're at the end, go to the start
        if (this.tabFocus >= this.tabs.length) {
          this.tabFocus = 0;
        }
        break;

      case KEY_CODES.ARROW_LEFT:
      case KEY_CODES.ARROW_LEFT_IE11:
        // Make the current tab element untabbable
        this.tabs[this.tabFocus].setAttribute('tabindex', -1);
        // Move focus to the left
        this.tabFocus--;
        // If we're at the start, move to the end
        if (this.tabFocus < 0) {
          this.tabFocus = this.tabs.length - 1;
        }
        break;

      default:
        break;
    }

    // Set the tabindex to 0 to make the tab focusable, then focus it
    if (this.tabs[this.tabFocus]) {
      this.tabs[this.tabFocus].setAttribute('tabindex', 0);
      this.tabs[this.tabFocus].focus();
    }
  }

  static init() {
    new Tabs();
  }
}

export default Tabs;
